import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  getDocs,
  Timestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { getCurrentUser } from './authService';

export interface DonationData {
  userId: string | null;
  donationType: 'registered' | 'anonymous';
  donationFrequency: 'one-time' | 'monthly' | 'quarterly' | 'yearly';
  project: string;
  amount: number;
  paymentMethod: 'card' | 'upi' | 'razorpay' | 'paypal';
  paymentStatus: string;
  transactionId?: string;
  donationId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  testPayment?: boolean;
  createdAt?: Timestamp | Date;
}

export interface DonationRecord extends DonationData {
  id: string;
}

/**
 * Save donation to Firestore
 */
export const saveDonationToFirestore = async (donationData: Omit<DonationData, 'createdAt'>): Promise<{ success: boolean; donationId?: string; error?: string }> => {
  try {
    const user = getCurrentUser();

    const donationToSave: Omit<DonationData, 'createdAt'> & { createdAt: Timestamp } = {
      ...donationData,
      userId: user?.uid || null,
      createdAt: Timestamp.now()
    };

    const docRef = await addDoc(collection(db, 'donations'), donationToSave);

    console.log('Donation saved successfully with ID:', docRef.id);

    return {
      success: true,
      donationId: docRef.id
    };
  } catch (error: any) {
    console.error('Error saving donation to Firestore:', error);
    return {
      success: false,
      error: error.message || 'Failed to save donation'
    };
  }
};

/**
 * Get donations for current user
 */
export const getUserDonations = async (): Promise<{ success: boolean; donations?: DonationRecord[]; error?: string }> => {
  try {
    const user = getCurrentUser();

    if (!user) {
      return {
        success: false,
        error: 'User not authenticated'
      };
    }

    const donationsQuery = query(
      collection(db, 'donations'),
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(donationsQuery);

    const donations: DonationRecord[] = [];
    querySnapshot.forEach((doc) => {
      donations.push({
        id: doc.id,
        ...doc.data() as DonationData
      });
    });

    return {
      success: true,
      donations
    };
  } catch (error: any) {
    console.error('Error fetching user donations:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch donations'
    };
  }
};

/**
 * Get all donations (admin only - for future use)
 */
export const getAllDonations = async (): Promise<{ success: boolean; donations?: DonationRecord[]; error?: string }> => {
  try {
    const donationsQuery = query(
      collection(db, 'donations'),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(donationsQuery);

    const donations: DonationRecord[] = [];
    querySnapshot.forEach((doc) => {
      donations.push({
        id: doc.id,
        ...doc.data() as DonationData
      });
    });

    return {
      success: true,
      donations
    };
  } catch (error: any) {
    console.error('Error fetching all donations:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch donations'
    };
  }
};

